import * as React from 'react';
import { cn, formatCurrency } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import type { Budget, Transaction } from '@/types';

interface BudgetWithDepth {
  budget: Budget;
  depth: number;
}

function collectBudgetTree(root: Budget, depth = 0): BudgetWithDepth[] {
  const result: BudgetWithDepth[] = [{ budget: root, depth }];
  for (const child of root.children ?? []) {
    result.push(...collectBudgetTree(child, depth + 1));
  }
  return result;
}

interface MoveTransactionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: Transaction | null;
  rootBudget: Budget;
  onMove: (targetBudgetId: string) => Promise<void>;
  isMoving?: boolean;
}

export function MoveTransactionDialog({
  open,
  onOpenChange,
  transaction,
  rootBudget,
  onMove,
  isMoving = false,
}: MoveTransactionDialogProps) {
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  const options = React.useMemo(
    () =>
      collectBudgetTree(rootBudget).filter(
        ({ budget }) => budget.id !== transaction?.budgetId
      ),
    [rootBudget, transaction?.budgetId]
  );

  React.useEffect(() => {
    if (!open) {
      setSelectedId(null);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (!selectedId) return;
    try {
      await onMove(selectedId);
      onOpenChange(false);
    } catch {
      // Keep dialog open on failure; parent shows toast
    }
  };

  const label = transaction?.merchant || transaction?.note || 'Transaction';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Move to budget</DialogTitle>
          <DialogDescription>
            Choose a budget for <span className="font-medium text-foreground">{label}</span>
          </DialogDescription>
        </DialogHeader>

        <div
          role="listbox"
          aria-label="Target budgets"
          className="border border-input rounded-lg bg-background overflow-y-auto max-h-64 p-1 space-y-0.5"
        >
          {options.map(({ budget, depth }) => {
            const selected = selectedId === budget.id;
            return (
              <button
                key={budget.id}
                type="button"
                role="option"
                aria-selected={selected}
                disabled={isMoving}
                className={cn(
                  'flex w-full items-center gap-2 py-2 pr-3 text-sm rounded-md text-left transition-colors',
                  'hover:bg-muted/60 disabled:opacity-50',
                  selected && 'bg-primary/10 text-primary font-medium'
                )}
                style={{ paddingLeft: `${8 + depth * 16}px` }}
                onClick={() => setSelectedId(budget.id)}
              >
                <span className="truncate flex-1 min-w-0">{budget.name}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatCurrency(budget.amount, budget.currency)}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            className="px-4 py-2 text-sm rounded-md border border-input hover:bg-muted"
            onClick={() => onOpenChange(false)}
            disabled={isMoving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            onClick={handleConfirm}
            disabled={!selectedId || isMoving}
          >
            {isMoving ? 'Moving…' : 'Move'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
